import { useState, useEffect } from "react";
import {
  Mail, Search, Download, Loader2, Users, Calendar, Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("es-HN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

export default function AdminNewsletter() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchSubscribers();
  }, [page, limit]);

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/subscribers?skip=${(page - 1) * limit}&limit=${limit}`);
      setSubscribers(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching subscribers:", error);
      toast.error("Error al cargar suscriptores");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (subscribers.length === 0) {
      toast.error("No hay suscriptores para exportar");
      return;
    }
    
    const headers = ["Email", "Fecha de suscripción"];
    const csvContent = [
      headers.join(","),
      ...subscribers.map(s => `${s.email},${formatDate(s.subscribed_at)}`)
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `suscriptores_newsletter_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    toast.success("CSV exportado");
  };

  const filteredSubscribers = subscribers.filter(s =>
    s.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-brand-text flex items-center gap-2">
              <Mail className="h-6 w-6 text-primary" />
              Newsletter
            </h1>
            <p className="text-brand-textSecondary">
              Lista de suscriptores al boletín
            </p>
          </div>
          <Button onClick={handleExportCSV} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-text">{subscribers.length}</p>
                <p className="text-sm text-gray-500">Total suscriptores</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-text">
                  {subscribers.filter(s => {
                    const subDate = new Date(s.subscribed_at);
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return subDate >= weekAgo;
                  }).length}
                </p>
                <p className="text-sm text-gray-500">Esta semana</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-text">
                  {subscribers.filter(s => {
                    const subDate = new Date(s.subscribed_at);
                    const monthAgo = new Date();
                    monthAgo.setMonth(monthAgo.getMonth() - 1);
                    return subDate >= monthAgo;
                  }).length}
                </p>
                <p className="text-sm text-gray-500">Este mes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar por email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredSubscribers.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">
              {searchQuery ? "No se encontraron resultados" : "No hay suscriptores"}
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              {searchQuery ? "Intenta con otro término de búsqueda" : "Los suscriptores aparecerán aquí cuando se registren"}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Fecha de suscripción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubscribers.map((subscriber, index) => (
                  <TableRow key={subscriber.email}>
                    <TableCell className="text-gray-500">{index + 1}</TableCell>
                    <TableCell className="font-medium">{subscriber.email}</TableCell>
                    <TableCell className="text-gray-500">
                      {formatDate(subscriber.subscribed_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="newsletter-pagination"
          label="suscriptores"
        />
    </AdminLayout>
  );
}
